/**
 * Video Reaction Reducer (Schema Restructure — DB-001a/DB-001b)
 *
 * State shape:
 *   state.emotes = {
 *     reactions: {
 *       [videoId]: {
 *         counts:    { mindblownCount, valuedCount, insightCount, challengeCount, normalCount },
 *         userState: { activeReaction: string|null }
 *       }
 *     },
 *     loading: false,
 *     error:   null
 *   }
 *
 * Mounted as `emotes` in rootReducer (backward-compat key name kept).
 */
import { VideoReactionActions } from './actions';

const EMPTY_COUNTS = {
  mindblownCount: 0, valuedCount: 0,
  insightCount: 0, challengeCount: 0, normalCount: 0
};
const EMPTY_USER_STATE = { activeReactions: {} };

const initialState = {
  reactions: {},    // { [videoId]: { counts, userState } }
  loading: false,
  error: null
};

const videoEmoteReducer = (state = initialState, action) => {
  // DEBUG — log every action that touches this reducer
  if (action.type && action.type.includes('REACTION')) {
    const us = action.payload?.userState;
    const pus = action.payload?.previousUserState;
    console.log(`[GLOW REDUCER] ${action.type}`, {
      mindblownActive: us?.activeReactions?.mindblown,
      prevMindblown: pus?.activeReactions?.mindblown,
      userState: us,
    });
  }

  switch (action.type) {

    // ── Load reactions ────────────────────────────────────
    case VideoReactionActions.LOAD_REACTIONS_REQUEST:
      return { ...state, loading: true, error: null };

    case VideoReactionActions.LOAD_REACTIONS_SUCCESS: {
      const { videoId, counts, userState } = action.payload;
      return {
        ...state,
        loading: false,
        reactions: {
          ...state.reactions,
          [videoId]: {
            counts: { ...EMPTY_COUNTS, ...counts },
            userState: userState ? { ...EMPTY_USER_STATE, ...userState } : { ...EMPTY_USER_STATE }
          }
        }
      };
    }

    case VideoReactionActions.LOAD_REACTIONS_FAILURE:
      return { ...state, loading: false, error: action.payload.error };

    // ── Toggle reaction (optimistic) ──────────────────────
    case VideoReactionActions.TOGGLE_REACTION_REQUEST:
      return { ...state, loading: true, error: null };

    case VideoReactionActions.TOGGLE_REACTION_OPTIMISTIC: {
      const { videoId, counts, userState } = action.payload;
      return {
        ...state,
        reactions: {
          ...state.reactions,
          [videoId]: {
            counts: { ...EMPTY_COUNTS, ...counts },
            userState: { ...EMPTY_USER_STATE, ...userState }
          }
        }
      };
    }

    case VideoReactionActions.TOGGLE_REACTION_SUCCESS: {
      const { videoId, counts, userState } = action.payload;
      return {
        ...state,
        loading: false,
        reactions: {
          ...state.reactions,
          [videoId]: {
            counts: { ...EMPTY_COUNTS, ...counts },
            userState: userState ? { ...EMPTY_USER_STATE, ...userState } : (state.reactions[videoId]?.userState || { ...EMPTY_USER_STATE })
          }
        }
      };
    }

    case VideoReactionActions.TOGGLE_REACTION_FAILURE: {
      const { videoId, previousCounts, previousUserState } = action.payload;
      return {
        ...state,
        loading: false,
        error: action.payload.error,
        reactions: {
          ...state.reactions,
          [videoId]: {
            counts: previousCounts ? { ...EMPTY_COUNTS, ...previousCounts } : (state.reactions[videoId]?.counts || { ...EMPTY_COUNTS }),
            userState: previousUserState ? { ...EMPTY_USER_STATE, ...previousUserState } : (state.reactions[videoId]?.userState || { ...EMPTY_USER_STATE })
          }
        }
      };
    }

    // ── Housekeeping ──────────────────────────────────────
    case VideoReactionActions.CLEAR_VIDEO_REACTIONS: {
      const { videoId } = action.payload;
      const { [videoId]: _removed, ...remaining } = state.reactions;
      return {
        ...state,
        reactions: remaining,
        error: null
      };
    }

    default:
      return state;
  }
};

export default videoEmoteReducer;
